import { useEffect, useState } from 'react';
import { Box, Spinner, Alert, Button, Text } from '@nimbus-ds/components';
import { useNavigate } from 'react-router-dom';
import { n8nConfigApi, N8nConfig } from '../services/api';

const Home: React.FC = () => {
  const [config, setConfig] = useState<N8nConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [showAlternatives, setShowAlternatives] = useState(false);
  const navigate = useNavigate();

  const openN8nInNewTab = () => {
    if (config?.host) {
      window.open(config.host, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    n8nConfigApi.get()
      .then((res) => {
        setConfig(res.data);
      })
      .catch((err) => {
        console.error('Failed to load data:', err);
        setError('Error al cargar la configuración');
      })
      .finally(() => setLoading(false));
  }, []);

  // Show alternatives after a delay if iframe seems stuck on login
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAlternatives(true);
    }, 5000); // Show after 5 seconds

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <Box
        height="100vh"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="4">
        <Alert appearance="danger" title="Error">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!config?.host) {
    return (
      <Box padding="4">
        <Alert appearance="warning" title="Configuración requerida">
          Necesitás configurar la conexión a n8n antes de continuar.
        </Alert>
        <Box marginTop="4">
          <Button onClick={() => navigate('/settings')}>
            Ir a Configuración
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Header */}
      <Box
        padding="2"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor="neutral-surface"
        borderColor="neutral-interactive"
        borderStyle="solid"
        borderWidth="none"
        borderBottomWidth="1"
      >
        <Box display="flex" alignItems="center" gap="2">
          {showAlternatives && (
            <Text fontSize="caption" color="neutral-textLow">
              ¿Problemas con el login?
            </Text>
          )}
        </Box>
        <Box display="flex" gap="2">
          <Button appearance="primary" onClick={openN8nInNewTab}>
            Abrir en nueva pestaña
          </Button>
          <Button appearance="transparent" onClick={() => navigate('/webhooks')}>
            Webhooks
          </Button>
          <Button appearance="transparent" onClick={() => navigate('/settings')}>
            Configuración
          </Button>
        </Box>
      </Box>

      {/* n8n iframe */}
      <Box flex="1" position="relative">
        {iframeLoading && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            style={{ transform: 'translate(-50%, -50%)' }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" gap="2">
              <Spinner size="large" />
              <Text fontSize="caption" color="neutral-textLow">Cargando n8n...</Text>
            </Box>
          </Box>
        )}
        <iframe
          src={config.host}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            display: iframeLoading ? 'none' : 'block',
          }}
          onLoad={() => setIframeLoading(false)}
          title="n8n Workflows"
          allow="clipboard-read; clipboard-write; storage-access"
        />
      </Box>

      {/* Help banner - shows if iframe might be stuck */}
      {showAlternatives && !iframeLoading && (
        <Box
          padding="2"
          backgroundColor="warning-surface"
          borderColor="warning-interactive"
          borderStyle="solid"
          borderWidth="none"
          borderTopWidth="1"
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Text fontSize="caption">
              Si ves la pantalla de login repetidamente, usá "Abrir en nueva pestaña" para mejor experiencia.
            </Text>
            <Button appearance="transparent" onClick={() => setShowAlternatives(false)}>
              Ocultar
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Home;
